import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      const xPos = (clientX / innerWidth - 0.5) * 20;
      const yPos = (clientY / innerHeight - 0.5) * 20;
      
      const elements = heroRef.current.querySelectorAll('.parallax-element');
      elements.forEach((el, index) => {
        const speed = (index + 1) * 0.5;
        (el as HTMLElement).style.transform = `translate(${xPos * speed}px, ${yPos * speed}px)`;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
      {/* Cinematic Video Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/60 to-[#0A0A0A] z-10"></div>
        <img
          src="https://public.readdy.ai/ai/img_res/5cfa5ccabd24dca932b6962ac0a1bdcc.jpg"
          alt="Paris Cinematic"
          className="w-full h-full object-cover object-center scale-105 animate-slow-zoom"
          loading="eager"
        />
      </div>

      {/* 3D Floating Elements with Parallax */}
      <div className="absolute inset-0 pointer-events-none z-20">
        {/* Gold particles */}
        <div className="parallax-element absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#FFD700]/30 to-[#FFA500]/30 rounded-full blur-xl animate-float"></div>
        <div className="parallax-element absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-[#C0C0C0]/20 to-[#FFD700]/20 rounded-full blur-lg animate-float-delayed"></div>
        <div className="parallax-element absolute bottom-40 left-20 w-24 h-24 bg-gradient-to-br from-[#FFA500]/20 to-[#FFD700]/20 rounded-full blur-2xl animate-pulse"></div>
        
        {/* 3D Glass Cards */}
        <div className="parallax-element absolute top-32 right-32 transform rotate-12 animate-float">
          <div className="w-32 h-40 bg-gradient-to-br from-white/5 to-white/10 rounded-2xl backdrop-blur-md border border-white/20 shadow-2xl"></div>
        </div>
        <div className="parallax-element absolute bottom-32 left-32 transform -rotate-12 animate-float-delayed">
          <div className="w-28 h-36 bg-gradient-to-br from-[#FFD700]/10 to-[#FFA500]/10 rounded-2xl backdrop-blur-md border border-[#FFD700]/30 shadow-2xl"></div>
        </div>

        {/* Neon lines */}
        <div className="parallax-element absolute top-1/2 left-0 w-64 h-px bg-gradient-to-r from-transparent via-[#FFD700]/50 to-transparent"></div>
        <div className="parallax-element absolute top-1/3 right-0 w-48 h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/30 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-30 text-center max-w-6xl mx-auto px-4">
        {/* Main Title with 3D Holographic Effect */}
        <div className="mb-8 animate-fade-in-up">
          <h1 
            className="text-6xl md:text-8xl lg:text-9xl font-black mb-4 relative inline-block"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <span className="absolute inset-0 blur-2xl bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] opacity-50"></span>
            <span className="relative bg-gradient-to-r from-[#FFD700] via-white to-[#C0C0C0] bg-clip-text text-transparent">
              VYBE PARIS
            </span>
          </h1>
          
          {/* Animated underline */}
          <div className="h-1 w-48 mx-auto bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-pulse"></div>
        </div>
        
        {/* Subtitle with cinematic effect */}
        <p 
          className="text-2xl md:text-4xl text-white/90 mb-6 animate-fade-in-up font-light tracking-wide"
          style={{ 
            fontFamily: 'Poppins, sans-serif',
            animationDelay: '0.2s',
            textShadow: '0 0 40px rgba(255, 215, 0, 0.3)'
          }}
        >
          Créateur d'expériences, d'émotions et de connexions
        </p>

        <p 
          className="text-lg md:text-xl text-white/70 mb-12 animate-fade-in-up max-w-3xl mx-auto"
          style={{ animationDelay: '0.4s' }}
        >
          Trois univers, une vision
        </p>

        {/* CTA Buttons with Premium 3D Effects */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <Link
            to="/events"
            className="group relative px-10 py-5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0A0A0A] font-bold rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#FFD700]/50"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <span className="relative z-10 flex items-center gap-3">
              <i className="ri-sparkling-2-fill text-2xl"></i>
              Explorer les univers VYBE
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform"></i>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFA500] to-[#FFD700] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </Link>
        </div>

        {/* Three Pillars Preview */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
          {[
            { 
              title: 'VYBE ÉVENT', 
              icon: 'ri-goblet-fill', 
              desc: 'Événements d\'exception',
              link: '/events',
              gradient: 'from-purple-500/20 to-pink-500/20',
              border: 'border-purple-500/50'
            },
            { 
              title: 'VYBE BY QUAD', 
              icon: 'ri-riding-fill', 
              desc: 'Aventures urbaines',
              link: '/quadlux',
              gradient: 'from-orange-500/20 to-red-500/20',
              border: 'border-orange-500/50'
            },
            { 
              title: 'VYBE AGENCY', 
              icon: 'ri-palette-fill', 
              desc: 'Création digitale',
              link: '/vybe',
              gradient: 'from-blue-500/20 to-cyan-500/20',
              border: 'border-blue-500/50'
            }
          ].map((pillar, index) => (
            <Link
              key={pillar.title}
              to={pillar.link}
              className={`group relative p-8 bg-gradient-to-br ${pillar.gradient} backdrop-blur-xl rounded-2xl border ${pillar.border} hover:scale-105 transition-all duration-500 hover:shadow-2xl`}
              style={{ animationDelay: `${1 + index * 0.1}s` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
              <i className={`${pillar.icon} text-5xl text-white/80 mb-4 block group-hover:scale-110 transition-transform`}></i>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {pillar.title}
              </h3>
              <p className="text-white/60 text-sm">{pillar.desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Scroll Indicator with Premium Animation */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-30 animate-bounce">
        <div className="relative">
          <div className="absolute inset-0 blur-xl bg-[#FFD700]/50 rounded-full"></div>
          <div className="relative w-14 h-14 rounded-full border-2 border-[#FFD700] flex items-center justify-center bg-[#0A0A0A]/50 backdrop-blur-sm">
            <i className="ri-arrow-down-line text-[#FFD700] text-2xl animate-pulse"></i>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slow-zoom {
          0%, 100% { transform: scale(1.05); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-slow-zoom {
          animation: slow-zoom 20s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .parallax-element {
          transition: transform 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
